import { defineComponent, shallowRef } from "vue";
import InlineSvg from "vue-inline-svg";
import Tuple from "@common/vue/Tuple.vue";
import FlightCard from "./vue/card/FlightCard.vue";
import AirportCard from "./vue/card/AirportCard.vue";
import StateSvg from "@common/vue/StateSvg.vue";
import LiveOverlay from "./vue/overlay/LiveOverlay.vue";
import StatsOverlay from "./vue/overlay/stats/StatsOverlay.vue";
import SearchOverlay from "./vue/overlay/search/SearchOverlay.vue";
import WorldOverlay from "./vue/overlay/world/WorldOverlay.vue";
import SettingsOverlay from "./vue/overlay/settings/SettingsOverlay.vue";
import ServerOverlay from "./vue/overlay/server/ServerOverlay.vue";
import FlightOverlay from "./vue/overlay/flight/FlightOverlay.vue";
import FullLiveOverlay from "./vue/overlay/FullLiveOverlay.vue";
import { SERVER_INTERVAL_MS, TIMEOUT_DELAY_MS } from "./LiveParams";
import { liveStore } from "./store/LiveStore";
import gsap from "gsap";
import LoadingIndicator from "@common/vue/LoadingIndicator.vue";
import LiveUniverse from "./vue/universe/LiveUniverse.vue";
import IdleTracker from "idle-tracker";
import { useToast } from "vue-toastification";
import AirportOverlay from "./vue/overlay/airport/AirportOverlay.vue";
import { Settings, SettingsItem } from "./settings/Settings";
import Mapbox from "./vue/mapbox/Mapbox.vue";
import { SET_IS_OVERLAY_VISIBLE } from "app/store/AppStoreMutations";
import { UPDATE_CURRENT_SERVER_DETAIL, UPDATE_STATE_WITH_IDS } from "./store/LiveActions";
import AccountOverlay from "app/vue/overlay/auth/AccountOverlay.vue";
export default defineComponent({
    components: {
        Tuple,
        FlightCard,
        AirportCard,
        InlineSvg,
        StateSvg,
        LiveOverlay,
        LoadingIndicator,
        LiveUniverse,
        FullLiveOverlay,
        Mapbox
    },
    data: function () {
        return {
            isUniverseActive: true,
            isVisibleOverlay: false,
            isVisibleFullOverlay: false,
            overlayIconSrc: "",
            overlayTitle: "",
            overlayBody: {},
            serversIntervalId: null,
            serverCountInfo: {
                userCount: 0,
                userCountAnimated: 0,
                userMax: 0,
                userMaxAnimated: 0,
                percentage: 0,
                percentageAnimated: 0
            },
        };
    },
    computed: {
        isFlightCardOn: function () {
            const isFlightCardOn = liveStore.getters.getIsFlightFocus;
            return isFlightCardOn;
        },
        isCardLoading: function () {
            const isLoading = liveStore.getters.getIsFlightAirportCardLoading;
            return isLoading;
        },
        currentServerName: function () {
            const server = liveStore.getters.getCurrentServerSimple;
            if (server == null) {
                return "Unknown Server";
            }
            return server.name;
        },
        currentServerCounts: function () {
            const server = liveStore.getters.getCurrentServerSimple;
            if (server == null) {
                return "??? / ??? (??%)";
            }
            this.$data.serverCountInfo.userCount = server.userCount;
            this.$data.serverCountInfo.userMax = server.userMax;
            this.$data.serverCountInfo.percentage = Math.round((server.userCount / server.userMax) * 100);
            const userCountAnimated = Math.floor(this.$data.serverCountInfo.userCountAnimated);
            const userMaxAnimated = Math.floor(this.$data.serverCountInfo.userMaxAnimated);
            const percentageAnimated = Math.floor(this.$data.serverCountInfo.percentageAnimated);
            return `${userCountAnimated} / ${userMaxAnimated} (${percentageAnimated}%)`;
        },
        currentFlights: function () {
            const server = liveStore.getters.getCurrentServerDetail;
            if (server == null)
                return;
            return server.flights;
        },
        currentAirports: function () {
            const server = liveStore.getters.getCurrentServerDetail;
            if (server == null)
                return;
            return server.activeAirports;
        },
        currentAirportDetail: function () {
            const airport = liveStore.getters.getCurrentAirportDetail;
            if (airport == null)
                return;
            return airport;
        },
        currentFlightId: function () {
            const flight = liveStore.getters.getCurrentFlightSimple;
            if (flight == null)
                return "";
            return flight.id;
        },
        currentFlightDetail: function () {
            const flight = liveStore.getters.getCurrentFlightDetail;
            return flight;
        },
        isTargetingFlight: function () {
            const isTargetingFlight = liveStore.getters.getTargetSwitch;
            return isTargetingFlight;
        }
    },
    watch: {
        "serverCountInfo.userCount": function (n) {
            gsap.to(this.serverCountInfo, { duration: 0.5, "userCountAnimated": Number(n) || 0 });
        },
        "serverCountInfo.userMax": function (n) {
            gsap.to(this.serverCountInfo, { duration: 0.5, "userMaxAnimated": Number(n) || 0 });
        },
        "serverCountInfo.percentage": function (n) {
            gsap.to(this.serverCountInfo, { duration: 0.5, "percentageAnimated": Number(n) || 0 });
        },
        currentFlightId: function (flightId) {
            const server = liveStore.getters.getCurrentServerSimple;
            if (server == null)
                return;
            this.$router.push({ path: "/live", query: { s: server.id, f: flightId } });
        },
        isVisibleOverlay: function (isVisibleOverlay) {
            this.$store.commit(SET_IS_OVERLAY_VISIBLE, isVisibleOverlay || this.isVisibleFullOverlay);
        },
        isVisibleFullOverlay: function (isVisibleFullOverlay) {
            this.$store.commit(SET_IS_OVERLAY_VISIBLE, isVisibleFullOverlay || this.isVisibleOverlay);
        }
    },
    methods: {
        onFlightFullScreen: function () {
            const flight = liveStore.getters.getCurrentFlightSimple;
            this.onClickFullOverlay("svg/flight.svg", flight == null ? "Unknown Callsign" : flight.callsign, FlightOverlay);
        },
        onAirportFullScreen: function () {
            const airport = liveStore.getters.getCurrentAirportSimple;
            this.onClickFullOverlay("svg/traffic.svg", airport == null ? "Unknown ICAO" : airport.icao, AirportOverlay);
        },
        startInterval: async function (serverId, flightId) {
            const update = async () => {
                const str = Settings.get(SettingsItem.AUTOMATIC_REFRESH);
                const isRefreshOn = str == null || str != "false";
                if (isRefreshOn) {
                    await liveStore.dispatch(UPDATE_CURRENT_SERVER_DETAIL);
                }
            };
            try {
                await liveStore.dispatch(UPDATE_STATE_WITH_IDS, { serverId, flightId });
            }
            catch (e) {
                this.toast.error(e);
            }
            this.serversIntervalId = window.setInterval(update, SERVER_INTERVAL_MS);
        },
        interuptInterval: function () {
            if (this.serversIntervalId != null) {
                window.clearInterval(this.serversIntervalId);
                this.serversIntervalId = null;
            }
        },
        onClickOverlay: function (src, title, body) {
            this.isVisibleOverlay = true;
            this.overlayIconSrc = src;
            this.overlayTitle = title;
            this.overlayBody = shallowRef(body);
        },
        onClickFullOverlay: function (src, title, body) {
            this.isVisibleFullOverlay = true;
            this.overlayIconSrc = src;
            this.overlayTitle = title;
            this.overlayBody = shallowRef(body);
        },
        onClickChart: function () {
            if (this.$store.getters.getIsSubscriptionActive) {
                const server = liveStore.getters.getCurrentServerSimple;
                const serverName = server == null ? "Unknown Server" : server.name;
                this.onClickOverlay("svg/charts.svg", `Stats - ${serverName}`, StatsOverlay);
            }
            else if (!this.$store.getters.getIsAuthenticated) {
                this.onClickOverlay("svg/account.svg", "Account", AccountOverlay);
            }
            else {
                this.$router.push("plus");
            }
        },
        onClickSearch: function () {
            const server = liveStore.getters.getCurrentServerSimple;
            const serverName = server == null ? "Unknown Server" : server.name;
            this.onClickOverlay("svg/search.svg", `Search - ${serverName}`, SearchOverlay);
        },
        onClickAirports: function () {
            const server = liveStore.getters.getCurrentServerSimple;
            const serverName = server == null ? "Unknown Server" : server.name;
            this.onClickOverlay("svg/traffic.svg", `Airports - ${serverName}`, WorldOverlay);
        },
        onClickSettings: function () {
            this.onClickOverlay("svg/settings.svg", "Settings", SettingsOverlay);
        },
        onClickServer: function () {
            this.onClickOverlay("svg/server.svg", "Servers", ServerOverlay);
        },
        onClickMap: function () {
            if (this.$store.getters.getIsSubscriptionActive) {
                this.isUniverseActive = false;
            }
            else if (!this.$store.getters.getIsAuthenticated) {
                this.onClickOverlay("svg/account.svg", "Account", AccountOverlay);
            }
            else {
                this.$router.push("plus");
            }
        },
        onClickEarth: function () {
            this.isUniverseActive = true;
        }
    },
    setup: function () {
        const toast = useToast();
        return {
            toast
        };
    },
    idleTracker: null,
    mounted: function () {
        let serverId = this.$route.query.s;
        if (serverId == undefined) {
            serverId = null;
        }
        let flightId = this.$route.query.f;
        if (flightId == undefined) {
            flightId = null;
        }
        this.startInterval(serverId, flightId);
        this.$options.idleTracker = new IdleTracker({
            timeout: TIMEOUT_DELAY_MS,
            onIdleCallback: ({ idle }) => {
                if (idle) {
                    this.$router.push("/live-timeout");
                }
            }
        });
        this.$options.idleTracker?.start();
    },
    unmounted: function () {
        this.interuptInterval();
        this.$options.idleTracker?.end();
    }
});
